import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/locations", label: "Texas Locations" },
  { to: "/learning", label: "Learning Center" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/resources", label: "Resources" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-all ${
        scrolled ? "backdrop-blur-md bg-[#0F172A]/95" : "bg-[#0F172A]"
      } border-b border-white/5`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid="navbar-logo" aria-label="Hurtler Environmental — home" className="flex items-center shrink-0 mr-6">
          <Logo size="md" showCaption />
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-2 py-2 text-[13px] font-medium rounded-md whitespace-nowrap transition-colors ${
                  isActive ? "text-white" : "text-white/70 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <Link to="/schedule" data-testid="navbar-schedule-cta" className="btn-primary text-sm !px-5 !py-2.5 whitespace-nowrap">
            Schedule Assessment
            <ChevronRight className="ml-1 w-4 h-4" />
          </Link>
        </div>

        <div className="xl:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="navbar-mobile-toggle"
                aria-label="Open menu"
                className="text-white p-2 rounded-md hover:bg-white/10"
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0F172A] text-white border-l border-white/10 w-80">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SheetDescription className="sr-only">Main site navigation links</SheetDescription>
              <div className="flex flex-col gap-1 mt-8">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `px-3 py-3 rounded-md text-base font-medium ${
                        isActive ? "bg-white/10 text-white" : "text-white/80 hover:bg-white/5"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <Link
                  to="/schedule"
                  data-testid="mobile-schedule-cta"
                  className="btn-primary mt-6 justify-center"
                >
                  Schedule Assessment
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
